import type { FC } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";

import { useBlocNext } from "@blac/react";
import { usePinch } from "@use-gesture/react";
import { css, keyframes } from "@emotion/css";
import styled from "@emotion/styled";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import translate from "src/lib/translate";

import { LoadingSpinner } from "../BlockingLoadingOverlay/BlockingLoadingOverlay";
import InDialog from "../InDialog/InDialog";
import PDFViewerBloc, {
  MIN_ZOOM,
  MAX_ZOOM,
  DEFAULT_ZOOM
} from "./PDFViewerBloc";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const MAX_CANVAS_WIDTH = 900;
const VISIBILITY_BUFFER_PX = 600;
const SCROLL_DEBOUNCE_MS = 60;
const WHEEL_ZOOM_DELTA = 0.1;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
`;

const pdfDialogStyles = css`
  --dialog-background: #f8f9fa;
  --toolbar-background: #fff;

  ion-toolbar {
    --border-width: 0;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06);
  }

  ion-content {
    &::part(scroll) {
      overflow: auto;
      padding: 0;
    }
  }
`;

const PDFContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: #f8f9fa;
  overflow: hidden;
`;

const CanvasContainer = styled.div`
  position: relative;
  flex: 1;
  overflow: auto;
  background: linear-gradient(180deg, #f0f2f5 0%, #e8eaed 100%);
  touch-action: pan-x pan-y;
  -webkit-overflow-scrolling: touch;
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const PageSlot = styled.div`
  flex-shrink: 0;
  margin-inline: auto;
  position: relative;
  background: #fff;
  border-radius: 4px;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.08),
    0 8px 24px rgba(0, 0, 0, 0.06);
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      110deg,
      transparent 30%,
      rgba(0, 0, 0, 0.03) 50%,
      transparent 70%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 2s ease-in-out infinite;
    pointer-events: none;
  }

  &:has(canvas)::after {
    display: none;
  }

  .page-content {
    position: relative;
    transform-origin: 0 0;
  }

  canvas {
    display: block;
  }

  .textLayer {
    position: absolute;
    text-align: initial;
    inset: 0;
    overflow: clip;
    line-height: 1;
    text-size-adjust: none;
    forced-color-adjust: none;
    transform-origin: 0 0;
    touch-action: auto;
    -webkit-user-select: text;
    user-select: text;
    --min-font-size: 1;
    --text-scale-factor: calc(var(--total-scale-factor) * var(--min-font-size));
    --min-font-size-inv: calc(1 / var(--min-font-size));

    span {
      padding: 10px;
      margin: -10px;
    }
  }

  .textLayer :is(span, br) {
    color: transparent;
    position: absolute;
    white-space: pre;
    cursor: text;
    transform-origin: 0% 0%;
  }

  .textLayer > :not(.markedContent),
  .textLayer .markedContent span:not(.markedContent) {
    --font-height: 0;
    --scale-x: 1;
    --rotate: 0deg;
    font-size: calc(var(--text-scale-factor) * var(--font-height));
    transform: rotate(var(--rotate)) scaleX(var(--scale-x))
      scale(var(--min-font-size-inv));
  }

  .textLayer .markedContent {
    display: contents;
  }

  .textLayer ::selection {
    background: rgba(0, 0, 255, 0.25);
  }

  .textLayer br::selection {
    background: transparent;
  }

  .textLayer .endOfContent {
    display: block;
    position: absolute;
    inset: 100% 0 0;
    z-index: 0;
    cursor: default;
    user-select: none;
  }

  .textLayer.selecting .endOfContent {
    top: 0;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.2s ease-out;
  background: rgba(248, 249, 250, 0.7);
  backdrop-filter: blur(2px);

  &[data-loading="true"] {
    opacity: 1;
  }
`;

const ControlBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 1rem;
  padding-bottom: calc(0.625rem + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  gap: 0.5rem;

  @media (min-width: 768px) {
    padding: 0.75rem 1.5rem;
    padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px));
  }
`;

const ZoomSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: var(--color-charcoal, #2d3436);
  cursor: pointer;
  transition: all 0.15s ease-out;
  -webkit-tap-highlight-color: transparent;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.05);
    }
  }

  &:active:not(:disabled) {
    background: rgba(0, 0, 0, 0.08);
    transform: scale(0.96);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

const ZoomDisplay = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  height: 36px;
  padding: 0 0.5rem;
  border: none;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.04);
  color: var(--color-charcoal, #2d3436);
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease-out;
  -webkit-tap-highlight-color: transparent;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.08);
    }
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    cursor: default;
  }
`;

const ErrorToast = styled.div`
  position: absolute;
  bottom: 5rem;
  left: 50%;
  transform: translateX(-50%);
  max-width: calc(100% - 2rem);
  padding: 0.875rem 1.25rem;
  background: var(--color-afternoon-yellow, #fff3cd);
  color: var(--color-charcoal, #2d3436);
  border-radius: 12px;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.1),
    0 1px 3px rgba(0, 0, 0, 0.08);
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
  z-index: 15;
  animation: ${slideUp} 0.3s ease-out;
`;

const PageIndicator = styled.div`
  position: absolute;
  bottom: 5rem;
  left: 1rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem 0.375rem 0.5rem;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 10px;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(0, 0, 0, 0.04);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-charcoal, #2d3436);
  pointer-events: none;
  user-select: none;
  z-index: 5;
`;

const PagesIcon: FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
    <rect x="1" y="1" width="5" height="16" rx="1.5" opacity="0.45" />
    <rect x="8" y="1" width="9" height="16" rx="1.5" />
  </svg>
);

const MinusIcon: FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const PlusIcon: FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ShareIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M7 11c-.93 0-1.395 0-1.776.102a3 3 0 0 0-2.122 2.121C3 13.606 3 14.07 3 15v1.2c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C5.28 21 6.12 21 7.8 21h8.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C21 18.72 21 17.88 21 16.2V15c0-.93 0-1.395-.102-1.777a3 3 0 0 0-2.122-2.12C18.396 11 17.93 11 17 11m-1-4-4-4m0 0L8 7m4-4v12"
    />
  </svg>
);

const DownloadIcon: FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const canShare =
  typeof navigator !== "undefined" && typeof navigator.share === "function";

function getScaledSize(
  dim: { width: number; height: number },
  containerWidth: number,
  zoom: number
) {
  const availableWidth = Math.min(containerWidth, MAX_CANVAS_WIDTH);
  const scale = (availableWidth / dim.width) * zoom;
  return { width: dim.width * scale, height: dim.height * scale };
}

const PDFViewer: FC = () => {
  const [state, bloc] = useBlocNext(PDFViewerBloc);
  const {
    openDocument,
    setContainerEl,
    close,
    zoomIn,
    zoomOut,
    resetZoom,
    download,
    share
  } = bloc;

  const containerRef = useRef<HTMLDivElement>(null);
  const slotRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const scrollDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [containerWidth, setContainerWidth] = useState(0);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const containerCallbackRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      containerRef.current = el;

      if (el) {
        setContainerEl(el);

        const measure = () => {
          const style = getComputedStyle(el);
          const padding =
            parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
          setContainerWidth(el.clientWidth - padding);
        };

        measure();
        resizeObserverRef.current = new ResizeObserver(measure);
        resizeObserverRef.current.observe(el);
      } else {
        setContainerWidth(0);
      }
    },
    [setContainerEl]
  );

  // Pinch-to-zoom (touch devices only; desktop ctrl+wheel handled by handleWheel)
  const zoomRef = useRef(state.zoom);
  zoomRef.current = state.zoom;

  usePinch(
    ({ offset: [s] }) => {
      bloc.setZoom(s);
    },
    {
      target: containerRef,
      scaleBounds: { min: MIN_ZOOM, max: MAX_ZOOM },
      from: () => [zoomRef.current, 0],
      pointer: { touch: true },
      eventOptions: { passive: false }
    }
  );

  // Load document
  useEffect(() => {
    if (state.blobUrl) {
      const loadingTask = pdfjsLib.getDocument(state.blobUrl);
      void openDocument(loadingTask);
    }
  }, [state.blobUrl, openDocument]);

  // Render visible pages
  const renderVisiblePages = useCallback(() => {
    const container = containerRef.current;
    if (!container || !containerWidth || !state.pageDimensions.length) {
      return;
    }

    const availableWidth = Math.min(containerWidth, MAX_CANVAS_WIDTH);
    const scrollTop = container.scrollTop;
    const viewTop = scrollTop - VISIBILITY_BUFFER_PX;
    const viewBottom =
      scrollTop + container.clientHeight + VISIBILITY_BUFFER_PX;

    let closestPage = 1;
    let closestDist = Infinity;
    const scrollMid = scrollTop + container.clientHeight / 2;

    for (let i = 1; i <= state.numPages; i++) {
      const slot = slotRefs.current.get(i);
      if (!slot) {
        continue;
      }

      const slotTop = slot.offsetTop;
      const slotBottom = slotTop + slot.offsetHeight;
      const slotMid = slotTop + slot.offsetHeight / 2;
      const dist = Math.abs(scrollMid - slotMid);
      if (dist < closestDist) {
        closestDist = dist;
        closestPage = i;
      }

      const isVisible = slotBottom > viewTop && slotTop < viewBottom;

      if (isVisible) {
        if (!bloc.isPageRendered(i)) {
          void bloc.renderPageToCanvas(i, slot, availableWidth);
        }
      } else {
        slot?.querySelector(".page-content")?.remove();
        bloc.clearPage(i);
      }
    }

    bloc.setCurrentPage(closestPage);
  }, [containerWidth, state.pageDimensions.length, state.numPages, bloc]);

  useLayoutEffect(() => {
    if (!state.pageDimensions.length || !containerWidth) {
      return;
    }
    const container = containerRef.current;
    if (!container || bloc.pendingScrollTop == null) {
      return;
    }

    bloc.isProgrammaticScroll = true;
    container.scrollTo(bloc.pendingScrollLeft ?? 0, bloc.pendingScrollTop);
    bloc.pendingScrollTop = null;
    bloc.pendingScrollLeft = null;
  }, [state.zoom, state.pageDimensions.length, containerWidth, bloc]);

  useEffect(() => {
    if (!state.pageDimensions.length || !containerWidth) {
      return;
    }
    renderVisiblePages();
  }, [
    state.zoom,
    renderVisiblePages,
    state.pageDimensions.length,
    containerWidth
  ]);

  const handleScroll = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    if (bloc.isProgrammaticScroll) {
      bloc.isProgrammaticScroll = false;
      renderVisiblePages();
      return;
    }

    clearTimeout(scrollDebounceRef.current);
    scrollDebounceRef.current = setTimeout(
      renderVisiblePages,
      SCROLL_DEBOUNCE_MS
    );
  }, [renderVisiblePages, bloc]);

  const handleWheel = (e: React.WheelEvent): void => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -WHEEL_ZOOM_DELTA : WHEEL_ZOOM_DELTA;
      bloc.setZoom(state.zoom + delta);
    }
  };

  const handleZoomDisplayClick = (): void => {
    if (state.zoom !== DEFAULT_ZOOM) {
      resetZoom();
    }
  };

  const zoomPercentage = Math.round(state.zoom * 100);

  return (
    <InDialog
      isOpen={state.isOpen}
      isFullScreen={true}
      onClose={close}
      backdropDismiss={false}
      title={state.filename ?? ""}
      width="calc(100vw - 2rem)"
      height="calc(100vh - 2rem)"
      noPadding
      popup
      simple
      className={pdfDialogStyles}
    >
      <PDFContainer>
        <CanvasContainer
          ref={containerCallbackRef}
          onScroll={handleScroll}
          onWheel={handleWheel}
        >
          {state.pageDimensions.map((dim, i) => {
            const pageNum = i + 1;
            if (!containerWidth) {
              return null;
            }
            const size = getScaledSize(dim, containerWidth, state.zoom);
            return (
              <PageSlot
                key={pageNum}
                data-page-slot={pageNum}
                ref={(el) => {
                  if (el) {
                    slotRefs.current.set(pageNum, el);
                  } else {
                    slotRefs.current.delete(pageNum);
                  }
                }}
                style={{ width: `${size.width}px`, height: `${size.height}px` }}
              />
            );
          })}
        </CanvasContainer>

        <LoadingOverlay data-loading={state.loading}>
          <LoadingSpinner />
        </LoadingOverlay>

        {state.numPages > 1 && (
          <PageIndicator>
            <PagesIcon />
            {state.currentPage} of {state.numPages}
          </PageIndicator>
        )}

        {state.error && <ErrorToast>{translate(state.error)}</ErrorToast>}

        <ControlBar>
          {canShare ? (
            <IconButton onClick={() => void share()} aria-label="Share">
              <ShareIcon />
            </IconButton>
          ) : (
            <IconButton onClick={download} aria-label="Download">
              <DownloadIcon />
            </IconButton>
          )}
          <ZoomSection>
            <IconButton
              onClick={zoomOut}
              disabled={state.zoom <= MIN_ZOOM}
              aria-label="Zoom out"
            >
              <MinusIcon />
            </IconButton>
            <ZoomDisplay
              onClick={handleZoomDisplayClick}
              disabled={state.zoom === DEFAULT_ZOOM}
              aria-label={
                state.zoom !== DEFAULT_ZOOM ? "Reset zoom" : undefined
              }
            >
              {zoomPercentage}%
            </ZoomDisplay>
            <IconButton
              onClick={zoomIn}
              disabled={state.zoom >= MAX_ZOOM}
              aria-label="Zoom in"
            >
              <PlusIcon />
            </IconButton>
          </ZoomSection>
        </ControlBar>
      </PDFContainer>
    </InDialog>
  );
};

export default PDFViewer;
